import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type CustomField, type CustomFieldValue } from '@air/api/types';
import { useToasts } from '@air/provider-toast';

import {
  type CreatePrivateAssetCustomFieldValueUpdateTaskParams,
  useCreatePrivateAssetCustomFieldValueUpdateTask,
} from '~/components/AssetCustomFieldValueUpdateTasks/hooks/useCreatePrivateAssetCustomFieldValueUpdateTask';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import {
  type ListCommonCustomFieldValuesResponse,
  useAssetCustomFieldValuesListCommon,
} from '~/hooks/customFields/useAssetCustomFieldValuesListCommon';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { type AssignedCustomFieldValue } from '~/swr-hooks/customFields/types';

export type ResolveInitialValueFn = (
  commonValue: ListCommonCustomFieldValuesResponse,
  field: CustomField,
  cfOptions?: CustomFieldValue[],
) => AssignedCustomFieldValue;

export type UseSelectAllCustomFieldModalParams = {
  field: CustomField;
  fallbackInitialValues: AssignedCustomFieldValue[];
  resolveInitialValue: ResolveInitialValueFn;
  cfOptions?: CustomFieldValue[];
};

export const useSelectAllCustomFieldModal = ({
  field,
  fallbackInitialValues,
  resolveInitialValue,
  cfOptions,
}: UseSelectAllCustomFieldModalParams) => {
  const { showToast } = useToasts();
  const { createPrivateAssetCustomFieldValueUpdateTask } = useCreatePrivateAssetCustomFieldValueUpdateTask();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
  const selectAllRootBoardId = useViewsSelectAllStore((state) => state.selectAllRootBoardId);
  const centralizedBoardId = useSelector(centralizedBoardIdSelector);
  const { isFlattenedView } = useIsFlattenedView();
  const boardId = selectAll && selectAllRootBoardId ? selectAllRootBoardId : centralizedBoardId;
  const includeDescendants = includeNestedItems || isFlattenedView;

  const { data: commonCustomFieldValue, isLoading } = useAssetCustomFieldValuesListCommon(
    { boardId, includeDescendants, customFieldId: field.id },
    { enabled: selectAll },
  );

  const initialValues = useMemo(() => {
    if (!selectAll) return fallbackInitialValues;

    if (!commonCustomFieldValue) {
      if (!isLoading) return fallbackInitialValues;

      return [
        {
          ...field,
          value: null,
          values: null,
          plainTextValue: undefined,
          dateValue: undefined,
        } satisfies AssignedCustomFieldValue,
      ];
    }

    return [resolveInitialValue(commonCustomFieldValue, field, cfOptions)];
  }, [selectAll, commonCustomFieldValue, isLoading, fallbackInitialValues, field, resolveInitialValue, cfOptions]);

  const initialStatus = selectAll ? commonCustomFieldValue?.status : undefined;
  const isLoadingInitialValues = selectAll && isLoading;

  const handleSelectAllSave = useCallback(
    async (taskParams: Omit<CreatePrivateAssetCustomFieldValueUpdateTaskParams, 'boardId' | 'includeDescendants'>) => {
      if (!boardId) {
        showToast('Unable to determine board - please try again');
        return;
      }
      await createPrivateAssetCustomFieldValueUpdateTask({
        ...taskParams,
        boardId,
        includeDescendants,
      });
    },
    [boardId, includeDescendants, showToast, createPrivateAssetCustomFieldValueUpdateTask],
  );

  return {
    selectAll,
    initialValues,
    initialStatus,
    isLoadingInitialValues,
    handleSelectAllSave,
  };
};
